import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';

interface Step {
  id: string;
  statusCode: PipelineStepStatusCode;
  incomingEdges: ReadonlyArray<{fromStep: {id: string}}>;
}

const UNFINISHED_STATUS_CODES: ReadonlySet<PipelineStepStatusCode> = new Set([
  PipelineStepStatusCode.RUNNING,
  PipelineStepStatusCode.WAITING,
  PipelineStepStatusCode.AI_PROPOSED,
]);

/**
 * Whether every step of an analysis has run as far as it will. A BLOCKED step is waiting for its parents,
 * so it will still run unless an upstream step failed: it has finished only when a parent failed or is
 * itself blocked by a failure. Steps in other branches keep running after one branch fails.
 */
export default function hasAnalysisFinished(steps: ReadonlyArray<Step>): boolean {
  if (steps.length === 0 || steps.some(({statusCode}) => UNFINISHED_STATUS_CODES.has(statusCode))) {
    return false;
  }
  const stepsById = new Map(steps.map((step) => [step.id, step]));
  const blockedByFailure = new Map<string, boolean>();
  const isBlockedByFailure = (step: Step, visiting: Set<string>): boolean => {
    const known = blockedByFailure.get(step.id);
    if (known != null) {
      return known;
    }
    visiting.add(step.id);
    const result = step.incomingEdges.some(({fromStep}) => {
      const parent = stepsById.get(fromStep.id);
      if (parent == null || visiting.has(parent.id)) {
        return false;
      }
      return (
        parent.statusCode === PipelineStepStatusCode.FAILED ||
        (parent.statusCode === PipelineStepStatusCode.BLOCKED && isBlockedByFailure(parent, visiting))
      );
    });
    visiting.delete(step.id);
    blockedByFailure.set(step.id, result);
    return result;
  };
  return steps.every(
    (step) => step.statusCode !== PipelineStepStatusCode.BLOCKED || isBlockedByFailure(step, new Set())
  );
}
